import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import API_BASE_URL from "../config";

const ExplorePage = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const categoryId = query.get("categoryId");
  const subcategoryId = query.get("subcategoryId");
  const categoryName = query.get("categoryName");
  const subcategoryName = query.get("subcategoryName");

  const [userLocation, setUserLocation] = useState({ city: "", state: "" });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [userInput, setUserInput] = useState({ city: "", state: "" });

  useEffect(() => {
    // Attempt to get browser location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
            );
            const data = await response.json();
            const city = data.city || "";
            const state = data.principalSubdivision || "";
            setUserLocation({ city, state });
            fetchServices(city, state);
          } catch (err) {
            console.error("Geolocation error:", err);
            setShowLocationForm(true);
            fetchServices();
          }
        },
        (err) => {
          console.error("Geolocation denied:", err);
          setShowLocationForm(true);
          fetchServices();
        }
      );
    } else {
      console.warn("Geolocation not supported");
      setShowLocationForm(true);
      fetchServices();
    }
  }, []);

  const fetchServices = async (city = "", state = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ city: encodeURIComponent(city), state: encodeURIComponent(state) });
      if (categoryId && subcategoryId) {
        params.append("categoryId", categoryId);
        params.append("subcategoryId", subcategoryId);
      }

      const response = await fetch(`${API_BASE_URL}/explore?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("API Response:", data); // Debug log

      // Ensure subcategories is an array for each category
      const categoriesWithIcons = data.map((category) => ({
        ...category,
        icon: getCategoryIcon(category.name),
        subcategories: category.subcategories
          ? Object.values(category.subcategories) // Convert object → array
          : [],
      }));

      setCategories(categoriesWithIcons);
      setLoading(false);
    } catch (err) {
      console.error("Fetch services error:", err);
      setError("Failed to load services. Please try again.");
      setLoading(false);
    }
  };

  const handleLocationSubmit = (e) => {
    e.preventDefault();
    setUserLocation(userInput);
    setShowLocationForm(false);
    fetchServices(userInput.city, userInput.state);
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      "Professional & Business Services": "bi-briefcase-fill",
      "Beauty, Grooming & Wellness": "bi-scissors",
      "Healthcare & Medical": "bi-heart-pulse",
      "Home & Repair Services": "bi-tools",
      "Real Estate & Property": "bi-house-fill",
      "Automobile & Transport": "bi-car-front-fill",
      "Events & Lifestyle": "bi-camera-fill",
      "Education & Training": "bi-book-fill",
      "Pet & Animal Care": "bi-paw",
      "Travel & Leisure": "bi-airplane-fill",
      "Food & Beverages": "bi-cup-straw",
      "Laundry & Maintenance": "bi-bucket-fill",
      "Childcare & Elderly Services": "bi-person-heart",
      "Miscellaneous / Local Utility": "bi-gear-fill",
    };
    return iconMap[categoryName] || "bi-gear-fill";
  };

  const renderStars = (stars) => {
    const fullStars = Math.floor(stars);
    const emptyStars = 5 - fullStars;
    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <i key={`full-${i}`} className="bi bi-star-fill text-warning"></i>
        ))}
        {[...Array(emptyStars)].map((_, i) => (
          <i key={`empty-${i}`} className="bi bi-star text-warning"></i>
        ))}
      </>
    );
  };

  return (
    <div className="explore-page py-5">
      <div className="container">
        <h1 className="fw700 mb-3 text-center">
          {categoryName && subcategoryName
            ? `Explore ${categoryName} - ${subcategoryName}`
            : "Explore Services Near You"}
        </h1>
        <p className="text-muted fs17 fw600 mb-4 text-center">
          {userLocation.city && userLocation.state
            ? `Showing services available in ${userLocation.city}, ${userLocation.state}`
            : "Please enter your location to find services near you"}
        </p>

        {showLocationForm && (
          <div className="row justify-content-center mb-5">
            <div className="col-md-6">
              <div className="card p-4">
                <h5 className="fw600 mb-3">Enter Your Location</h5>
                <form onSubmit={handleLocationSubmit}>
                  <div className="mb-3">
                    <label htmlFor="city" className="form-label">
                      City
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="city"
                      value={userInput.city}
                      onChange={(e) =>
                        setUserInput({ ...userInput, city: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="state" className="form-label">
                      State
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="state"
                      value={userInput.state}
                      onChange={(e) =>
                        setUserInput({ ...userInput, state: e.target.value })
                      }
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-success w-100">
                    Find Services
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && categories.length === 0 && (
          <div className="alert alert-info text-center">
            No services found for this location or category. Try a different city or state.
          </div>
        )}

        {!loading && !error && categories.length > 0 && (
          <div className="row g-4">
            {categories.map((category, index) => (
              <div className="col-md-6 col-lg-4" key={index}>
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title fw600 mb-3">
                      <i className={`bi ${category.icon} me-2 text-success`}></i>
                      {category.name}
                    </h5>
                    {Array.isArray(category.subcategories) &&
                    category.subcategories.length > 0 ? (
                      category.subcategories.map((subcat, idx) => (
                        <div key={idx} className="mb-3">
                          <h6 className="fw500 mb-2">{subcat.name}</h6>
                          <ul className="list-unstyled">
                            {subcat.companies &&
                            Array.isArray(subcat.companies) &&
                            subcat.companies.length > 0 ? (
                              subcat.companies.map((company, companyIdx) => (
                                <li key={companyIdx} className="mb-2">
                                  <Link
                                    to={`/company/${company.slug}`}
                                    className="text-decoration-none text-success"
                                  >
                                    <i className="bi bi-chevron-right me-2 text-muted"></i>
                                    {company.name}
                                  </Link>
                                  <div className="d-flex align-items-center mt-1">
                                    <span className="me-2">{renderStars(company.stars)}</span>
                                    <span>({company.average_rating.toFixed(1)})</span>
                                  </div>
                                </li>
                              ))
                            ) : (
                              <li className="text-muted">No companies available</li>
                            )}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">No subcategories available</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;